import React, { useState, useEffect, useMemo, useRef } from 'react';
import { STATUS_OPTIONS, OFFICES } from '../../../constants/calendar';
import { toAPIDate, formatDateWithWeekday, addDays ,formatTime, formatDateTime} from '../../../utils/dateUtils';
import { toast } from 'react-toastify';
import { getCurrentUser, getAuthHeaders } from '../../../utils/auth';
import { getApiUrl } from '../../../config/api';
import MessageThreadModal from './MessageThreadModal';
import './AppSendText.css';


const AppSendText = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointments, setSelectedAppointments] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [messageType, setMessageType] = useState('0');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [messageThreadModal, setMessageThreadModal] = useState(null);
  const [updatedRows, setUpdatedRows] = useState(new Set());
  const [reportText, setReportText] = useState('');
  const [showReport, setShowReport] = useState(false);

  // Prevents overlapping /api/appointments requests (manual fetch + SSE-triggered
  // quiet refetch could otherwise both fire and pile up on the DB pool)
  const isFetchingRef = useRef(false);
  const abortControllerRef = useRef(null);

  // Filter states
  const [officeFilter, setOfficeFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(-1);

  // Decode URL-encoded messages (e.g., "Hi+I+need..." -> "Hi I need...")
  const decodeMessage = (message) => {
    if (!message) return '';
    try {
      // Replace + with spaces and decode URI components
      return decodeURIComponent(message.replace(/\+/g, ' '));
    } catch (error) {
      // If decoding fails, just replace + with spaces
      return message.replace(/\+/g, ' ');
    }
  };

  // Fetch appointments for the selected date
  useEffect(() => {
    fetchAppointments();
  }, [selectedDate]);

  // Subscribe to SSE for real-time appointment updates (replaces 5-second polling)
  useEffect(() => {
    const formattedDate = toAPIDate(selectedDate);
    const source = new EventSource(getApiUrl(`/api/appointments/sse?date=${formattedDate}`));

    source.addEventListener('appointment_updated', () => {
      fetchAppointmentsQuietly();
    });

    return () => {
      source.close();
      abortControllerRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const fetchAppointments = async () => {
    // A new explicit fetch (date change, manual refresh) supersedes whatever's in flight
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    isFetchingRef.current = true;

    setLoading(true);
    setError(null);
    try {
      const formattedDate = toAPIDate(selectedDate);
      const url = getApiUrl(`/api/appointments?date=${formattedDate}`);
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        throw new Error('Failed to fetch appointments');
      }

      const data = await response.json();
      setAppointments(data);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError(err.message);
      console.error('Error fetching appointments:', err);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  // Fetch appointments without showing loading state (for polling)
  const fetchAppointmentsQuietly = async () => {
    // Skip if a fetch (manual or quiet) is already in flight instead of piling on
    if (isFetchingRef.current) return;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    isFetchingRef.current = true;

    try {
      const formattedDate = toAPIDate(selectedDate);
      const url = getApiUrl(`/api/appointments?date=${formattedDate}`);
      const response = await fetch(url, { signal: controller.signal });

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      // Check for appointment status changes
      // (new-SMS-reply detection removed along with LatestSMSMessage - the API no
      // longer joins SMS history here; see recent-incoming-messages/SMSResponsesSidebar
      // for the global new-message alert)
      const updatedAppointmentIds = new Set();
      data.forEach(newAppt => {
        const oldAppt = appointments.find(a => a.ID === newAppt.ID);
        if (oldAppt && oldAppt.AppointmentStatus !== newAppt.AppointmentStatus) {
          updatedAppointmentIds.add(newAppt.ID);

          // Show toast notification for status change
          toast.info(
            `${newAppt.ClientName}'s appointment status changed to: ${newAppt.AppointmentStatus}`,
            {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
        }
      });

      // Update appointments
      setAppointments(data);

      // Flash the updated rows
      if (updatedAppointmentIds.size > 0) {
        setUpdatedRows(updatedAppointmentIds);
        // Clear flash after animation completes
        setTimeout(() => {
          setUpdatedRows(new Set());
        }, 2000);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error polling appointments:', err);
    } finally {
      isFetchingRef.current = false;
    }
  };

  // Navigate to previous day
  const handlePrevDay = () => {
    setSelectedDate(addDays(selectedDate, -1));
  };

  // Navigate to next day
  const handleNextDay = () => {
    setSelectedDate(addDays(selectedDate, 1));
  };

  // Handle appointment selection
  const handleAppointmentSelect = (appointmentId) => {
    const newSelected = new Set(selectedAppointments);
    if (newSelected.has(appointmentId)) {
      newSelected.delete(appointmentId);
    } else {
      newSelected.add(appointmentId);
    }
    setSelectedAppointments(newSelected);
  };

  // Handle status change
  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const url = getApiUrl(`/api/appointments/${appointmentId}/status`);
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      // Update local state
      setAppointments(appointments.map(apt =>
        apt.ID === appointmentId ? { ...apt, AppointmentStatus: newStatus } : apt
      ));
    } catch (err) {
      setError(err.message);
      console.error('Error updating status:', err);
    }
  };

  // Get status badge class
  const getStatusClass = (status) => {
    const statusMap = {
      'Confirmed': 'status-confirmed',
      'Called': 'status-called',
      'Called - No answer': 'status-called',
      'Cancelled': 'status-cancelled',
      'Checked In': 'status-checked-in',
      'Booked': 'status-booked',
      'No Show': 'status-no-show',
      'Reschedule Requested': 'status-reschedule'
    };
    return statusMap[status] || 'status-default';
  };

  // Generate message based on type
  const generateMessage = (appointment) => {
    const { ClientName, MeetingWith, EventStart } = appointment;
    const firstName = ClientName ? ClientName.split(' ')[0] : 'Customer';
    const timeOnly = formatTime(EventStart);
    const appointmentDateTime = `${formatDateWithWeekday(selectedDate)} at ${timeOnly}`;

    switch (messageType) {
      case '1': // Confirmation
        return `${firstName}, thank you for scheduling your appointment with Dini Wigs for ${appointmentDateTime} with ${MeetingWith}. Please reply YES to confirm. Questions or need to reschedule? Call 845-262-1010. [ID:${appointment.ID}]`;
      case '2': // Reminder
        return `Reminder: ${firstName}, your appointment with Dini Wigs is scheduled for ${appointmentDateTime} with ${MeetingWith}. Please reply YES to confirm. Questions or need to reschedule? Call 845-262-1010. [ID:${appointment.ID}]`;
      case '3': // Custom
        return `${customMessage} [ID:${appointment.ID}]`;
      case '4': // Sendout Payment Reminder
        if (appointment.orderno) {
          return `Hi ${firstName}, your Dini Wigs order is on its way! Please note that payment is due by end of day. To avoid an automatic charge to your card on file, kindly complete your payment here: [invoice link]`;
        }
        return `${firstName} — No order number linked to this appointment. Cannot send payment reminder.`;
      default:
        return '';
    }
  };

  // Send messages to selected appointments
  const handleSendMessages = async () => {
    if (messageType === '0') {
      setError('Please select a message type');
      return;
    }

    if (selectedAppointments.size === 0) {
      setError('Please select at least one appointment');
      return;
    }

    if (messageType === '3' && !customMessage.trim()) {
      setError('Please enter a custom message');
      return;
    }

    setSending(true);
    setError(null);
    setSuccessMessage('');

    try {
      const selectedAppts = appointments.filter(apt =>
        selectedAppointments.has(apt.ID)
      );

      let successCount = 0;
      let failCount = 0;

      for (const appointment of selectedAppts) {
        try {
          // Validate phone number before sending
          if (!appointment.PhoneNum || appointment.PhoneNum.trim() === '') {
            console.warn(`⚠️  Skipping appointment ${appointment.ID} - missing phone number`);
            failCount++;
            continue;
          }

          if (messageType === '4') {
            // Sendout Payment Reminder — calls invoice-link API with the appointment's linked order
            if (!appointment.orderno) {
              console.warn(`⚠️  Skipping appointment ${appointment.ID} - no order number linked`);
              toast.warning(`${appointment.ClientName} has no order number linked — skipping`);
              failCount++;
              continue;
            }

            const firstName = appointment.ClientName ? appointment.ClientName.split(' ')[0] : 'Customer';
            const customMessage = `Hi ${firstName}, your Dini Wigs order is on its way! Please note that payment is due by end of day. To avoid an automatic charge to your card on file, kindly complete your payment here:`;

            const url = getApiUrl('/api/invoice-link/send-sms');
            const response = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                customerName: appointment.ClientName,
                customerPhone: appointment.PhoneNum,
                customerNo: appointment.ClientID,
                orderNumbers: [appointment.orderno],
                expiresInHours: 48,
                customMessage,
                sentBy: getCurrentUser()
              }),
            });

            if (response.ok) {
              successCount++;
              setAppointments(prev => prev.map(apt =>
                apt.ID === appointment.ID
                  ? { ...apt, ProgrammerComments: getMessageStatus(messageType) }
                  : apt
              ));
            } else {
              const errorData = await response.json();
              console.error(`Failed to send payment reminder to ${appointment.ClientName}:`, errorData);
              failCount++;
            }
            continue;
          }

          const message = generateMessage(appointment);
          const url = getApiUrl('/api/appointments/send-sms');

          console.log(`Sending SMS for appointment ${appointment.ID}:`, {
            hasPhoneNum: !!appointment.PhoneNum,
            phoneNum: appointment.PhoneNum,
            messageLength: message.length
          });

          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify({
              appointmentId: appointment.ID,
              phoneNumber: appointment.PhoneNum,
              message: message,
              messageType: messageType
            }),
          });

          if (response.ok) {
            successCount++;
            // Update the appointment's ProgrammerComments locally
            setAppointments(prev => prev.map(apt =>
              apt.ID === appointment.ID
                ? { ...apt, ProgrammerComments: getMessageStatus(messageType) }
                : apt
            ));
          } else {
            const errorData = await response.json();
            console.error(`Failed to send SMS to ${appointment.ClientName}:`, errorData);
            failCount++;
          }
        } catch (err) {
          console.error(`Failed to send to ${appointment.ClientName}:`, err);
          failCount++;
        }
      }

      setSuccessMessage(`Sent ${successCount} message(s) successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);

      // Clear selections after successful send
      if (successCount > 0) {
        setSelectedAppointments(new Set());
      }

      // Refresh appointments to get updated status
      setTimeout(() => {
        fetchAppointments();
      }, 1000);

    } catch (err) {
      setError('Failed to send messages: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  // Staff "names" in OFFICES that are really appointment-type placeholders, not people
  const NON_STAFF_MEETING_WITH = ['send out', 'send out lw', 'wash & set', 'wash and set'];

  // Build Lucie's daily confirmation-status report, grouped by staff:
  // STAFF NAME
  // Client Name confirmed 10:30 am ✅
  // Client Name no response T&C 10:30 am ❌
  const handleGenerateReport = () => {
    const groups = {};

    const calledStatuses = ['Called', 'Called - No answer'];

    const reportAppointments = officeFilter === 'all'
      ? appointments
      : appointments.filter((apt) => getOfficeForStaff(apt.MWuserID) === officeFilter);

    reportAppointments.forEach((apt) => {
      if (apt.AppointmentStatus !== 'Confirmed' && !calledStatuses.includes(apt.AppointmentStatus)) return;

      const staffName = (apt.MeetingWith || '').trim();
      if (!staffName || NON_STAFF_MEETING_WITH.includes(staffName.toLowerCase())) return;

      if (!groups[staffName]) groups[staffName] = [];
      groups[staffName].push(apt);
    });

    const staffNames = Object.keys(groups).sort((a, b) => {
      const aIsDini = a.toLowerCase() === 'dini';
      const bIsDini = b.toLowerCase() === 'dini';
      if (aIsDini && !bIsDini) return -1;
      if (bIsDini && !aIsDini) return 1;
      return a.localeCompare(b);
    });

    const sections = staffNames.map((staffName) => {
      const clients = [...groups[staffName]].sort(
        (a, b) => new Date(a.EventStart) - new Date(b.EventStart)
      );

      const lines = clients.map((apt) => {
        const time = formatTime(apt.EventStart).toLowerCase();
        return apt.AppointmentStatus === 'Confirmed'
          ? `${apt.ClientName} confirmed ${time} ✅`
          : `${apt.ClientName} no response T&C ${time} ❌`;
      });

      return `${staffName.toUpperCase()}\n${lines.join('\n')}`;
    });

    setReportText(sections.join('\n\n'));
    setShowReport(true);
  };

  const handleCopyReport = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      toast.success('Report copied to clipboard');
    } catch (err) {
      console.error('Error copying report:', err);
      toast.error('Failed to copy report');
    }
  };

  // Get message status text based on type
  const getMessageStatus = (type) => {
    switch (type) {
      case '1':
        return 'Confirmation message sent';
      case '2':
        return 'Reminder message sent';
      case '3':
        return 'Custom message sent';
      case '4':
        return 'Sendout payment reminder sent';
      default:
        return 'Message sent';
    }
  };

  // Handle opening message thread modal
  const handleOpenMessageThread = (appointment) => {
    setMessageThreadModal({
      phoneNumber: appointment.PhoneNum,
      customerName: appointment.ClientName,
      appointmentId: appointment.ID
    });
  };

  // Handle closing message thread modal
  const handleCloseMessageThread = () => {
    setMessageThreadModal(null);
  };

  // Get staff members for selected office
  const getStaffForOffice = () => {
    if (officeFilter === 'all') {
      return Object.values(OFFICES).flatMap(office => office.staff);
    }
    return OFFICES[officeFilter]?.staff || [];
  };

  // Get staff member by ID
  const getStaffMemberById = (id) => {
    for (const office of Object.values(OFFICES)) {
      const staff = office.staff.find(s => s.id === id);
      if (staff) return staff;
    }
    return null;
  };

  // Get office for staff member
  const getOfficeForStaff = (staffId) => {
    for (const [officeKey, office] of Object.entries(OFFICES)) {
      if (office.staff.some(s => s.id === staffId)) {
        return officeKey;
      }
    }
    return null;
  };

  // Filter and search appointments
  const filteredAppointments = useMemo(() => {
    return appointments.filter(apt => {
      // Office filter
      if (officeFilter !== 'all') {
        const staffOffice = getOfficeForStaff(apt.MWuserID);
        if (staffOffice !== officeFilter) return false;
      }

      // Staff filter
      if (staffFilter !== 'all') {
        if (apt.MWuserID !== parseInt(staffFilter)) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        if (apt.AppointmentStatus !== statusFilter) return false;
      }

      // Search query (searches: ClientName, PhoneNum, MeetingWith, Title)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const clientName = (apt.ClientName || '').toLowerCase();
        const phoneNum = (apt.PhoneNum || '').toLowerCase();
        const meetingWith = (apt.MeetingWith || '').toLowerCase();
        const title = (apt.Title || '').toLowerCase();

        if (!clientName.includes(query) &&
            !phoneNum.includes(query) &&
            !meetingWith.includes(query) &&
            !title.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [appointments, officeFilter, staffFilter, statusFilter, searchQuery]);

  // Customer search functionality
  const handleCustomerSearch = async () => {
    if (!customerSearchQuery.trim()) {
      setCustomerSearchResults([]);
      return;
    }

    setSearchingCustomers(true);
    try {
      const url = getApiUrl(`/api/appointments/customers/search?q=${encodeURIComponent(customerSearchQuery)}`);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to search customers');
      }

      const data = await response.json();
      setCustomerSearchResults(data.customers || []);
    } catch (err) {
      console.error('Error searching customers:', err);
      setError('Failed to search customers');
    } finally {
      setSearchingCustomers(false);
    }
  };

  // Debounce customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleCustomerSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearchQuery]);

  // Clear filters
  const handleClearFilters = () => {
    setOfficeFilter('all');
    setStaffFilter('all');
    setStatusFilter('all');
    setSearchQuery('');
  };

  // Reset selected index when search results change
  useEffect(() => {
    setSelectedCustomerIndex(-1);
  }, [customerSearchResults]);

  // Handle selecting a customer from search results
  const handleSelectCustomer = (customer) => {
    setMessageThreadModal({
      phoneNumber: customer.CustomerPhone,
      customerName: customer.CustomerFullName,
      appointmentId: null
    });
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setSelectedCustomerIndex(-1);
  };

  // Handle keyboard navigation in customer search
  const handleCustomerSearchKeyDown = (e) => {
    if (customerSearchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedCustomerIndex(prev =>
          prev < customerSearchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedCustomerIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedCustomerIndex >= 0 && selectedCustomerIndex < customerSearchResults.length) {
          handleSelectCustomer(customerSearchResults[selectedCustomerIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setCustomerSearchResults([]);
        setSelectedCustomerIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="app-send-text">
      {/* Animated blob background */}
      {/* <div className="slider-thumb"></div> */}

      {/* Date Navigation */}
        
        <h1>Appointment Management - Send Text Messages</h1>

         {/* Customer Search */}
          <div className="search-group-txt customer-search-group-txt">
            {/* <label htmlFor="customerSearch">Search All Customers:</label> */}
            <input
              id="customerSearch"
              type="text"
              value={customerSearchQuery}
              onChange={(e) => setCustomerSearchQuery(e.target.value)}
              onKeyDown={handleCustomerSearchKeyDown}
              placeholder="Search customer to open message thread..."
              className="search-input-txt"
            />
            {searchingCustomers && <span className="search-loading">Searching...</span>}

            {/* Customer Search Results Dropdown */}
            {customerSearchResults.length > 0 && (
              <div className="customer-search-results-dropdown">
                {customerSearchResults.map((customer, index) => (
                  <div
                    key={customer.ID}
                    className={`customer-result-item ${index === selectedCustomerIndex ? 'selected' : ''}`}
                    onClick={() => handleSelectCustomer(customer)}
                    onMouseEnter={() => setSelectedCustomerIndex(index)}
                  >
                    <div className="customer-result-name">{customer.CustomerFullName}</div>
                    <div className="customer-result-phone">{customer.CustomerPhone}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          

      {/* Error Display */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      {/* Filter and Search Section */}
      <div className="filter-search-container">
        {/* Filters Row */}
        <div className="filters-row">
          {/* Staff Filter */}
          <div className="filter-group-txt">
            <label htmlFor="staffFilter">Staff:</label>
            <select
              id="staffFilter"
              value={staffFilter}
              onChange={(e) => setStaffFilter(e.target.value)}
              className="filter-select-txt"
            >
              <option value="all">All Staff</option>
              {getStaffForOffice().map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="filter-group-txt">
            <label htmlFor="statusFilter">Status:</label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select-txt"
            >
              <option value="all">All Statuses</option>
              {STATUS_OPTIONS.map(status => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters Button */}
          <button
            onClick={handleClearFilters}
            className="clear-filters-btn"
            title="Clear all filters"
          >
            Clear Filters
          </button>

          <div className="date-navigation">
      
            <button
              onClick={handlePrevDay}
              className="nav-button-txt"
              aria-label="Previous day"
            >
              ◀
            </button>

            <div className="date-display">
              <input
                type="date"
                value={toAPIDate(selectedDate)}
                onChange={(e) => {
                  // Parse date as local to avoid timezone shift
                  const [year, month, day] = e.target.value.split('-').map(Number);
                  setSelectedDate(new Date(year, month - 1, day));
                }}
                className="date-input"
              />
            
            </div>

            <button
              onClick={handleNextDay}
              className="nav-button-txt"
              aria-label="Next day"
            >
              ▶
            </button>
          </div>
        </div>

        {/* Search Rows */}
        <div className="search-rows">
          {/* Current View Search */}
          <div className="search-group-txt">
            <label htmlFor="searchQuery">Search Today's Appointments:</label>
            <input
              id="searchQuery"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, phone, staff, or title..."
              className="search-input-txt"
            />
          </div>
        </div>
      </div>

      {/* Appointments Grid */}
      <div className="appointments-container">
        <div className="appointments-header">
          <h2>Appointments for {formatDateWithWeekday(selectedDate)}</h2>
          <div className="header-actions">
            <div className="selected-count">
              {filteredAppointments.length} of {appointments.length} appointments
              {selectedAppointments.size > 0 && ` | ${selectedAppointments.size} selected`}
            </div>
            <div className="filter-group-txt report-office-filter">
              <select
                id="officeFilter"
                value={officeFilter}
                onChange={(e) => setOfficeFilter(e.target.value)}
                className="filter-select-txt"
              >
                <option value="all">All Offices</option>
                <option value="pomona">Pomona</option>
                <option value="lakewood">Lakewood</option>
                <option value="israel">Israel</option>
              </select>
            </div>
            <button className="generate-report-btn" onClick={handleGenerateReport}>
              Generate Report
            </button>
          </div>
        </div>

        {showReport && (
          <div className="report-section">
            <div className="report-section-header">
              <h3>Daily Confirmation Report</h3>
              <div className="report-actions">
                <button className="copy-report-btn" onClick={handleCopyReport}>
                  Copy to Clipboard
                </button>
                <button className="close-report-btn" onClick={() => setShowReport(false)}>
                  Close
                </button>
              </div>
            </div>
            {reportText ? (
              <textarea
                className="report-textarea"
                value={reportText}
                readOnly
                rows={Math.min(30, reportText.split('\n').length + 2)}
              />
            ) : (
              <div className="report-empty">
                No confirmed or called appointments for this date yet.
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="loading">Loading appointments...</div>
        ) : filteredAppointments.length === 0 ? (
          <div className="no-appointments">
            {appointments.length === 0
              ? 'No appointments scheduled for this date.'
              : 'No appointments match the current filters.'}
          </div>
        ) : (
          <div className="appointments-table-wrapper">
            <table className="appointments-table">
              <thead>
                <tr>
                  <th className="select-col">Select</th>
                  <th>Client Name</th>
                  <th>Phone Number</th>
                  <th>Meeting With</th>
                  <th>Title</th>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Last Sent</th>
                  <th className="chat-col">Messages</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppointments.map((appointment) => (
                  <tr
                    key={appointment.ID}
                    className={`
                      ${selectedAppointments.has(appointment.ID) ? 'selected-row' : ''}
                      ${updatedRows.has(appointment.ID) ? 'flash-update' : ''}
                    `.trim()}
                  >
                    <td className="select-col">
                      <input
                        type="checkbox"
                        checked={selectedAppointments.has(appointment.ID)}
                        onChange={() => handleAppointmentSelect(appointment.ID)}
                        className="appointment-checkbox"
                      />
                    </td>
                    <td>{appointment.ClientName}</td>
                    <td>{appointment.PhoneNum}</td>
                    <td>{appointment.MeetingWith}</td>
                    <td>{appointment.Title}</td>
                    <td>{formatDateTime(appointment.EventStart)}</td>
                    <td className="status-cell">
                      {appointment.Title === 'Send Out' ? (
                        <span
                          className={`status-select ${getStatusClass(appointment.AppointmentStatus)}`}
                          title="Status is managed automatically for Send Out appointments"
                          style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', cursor: 'default' }}
                        >
                          {appointment.AppointmentStatus || 'Booked'}
                        </span>
                      ) : (
                        <select
                          value={appointment.AppointmentStatus || 'Booked'}
                          onChange={(e) => handleStatusChange(appointment.ID, e.target.value)}
                          className={`status-select ${getStatusClass(appointment.AppointmentStatus)}`}
                        >
                          {STATUS_OPTIONS.map(status => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="message-cell">
                      {decodeMessage(appointment.ProgrammerComments) || 'No messages'}
                    </td>
                    <td className="chat-col">
                      <button
                        className="chat-icon-btn"
                        onClick={() => handleOpenMessageThread(appointment)}
                        title="View message history"
                        disabled={!appointment.PhoneNum}
                      >
                        💬
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      
      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {/* Messaging Panel */}
      <div className="messaging-panel">
        <div className="messaging-header">
          <h3>Send Messages</h3>
          <span className="selected-info">
            {selectedAppointments.size} appointment{selectedAppointments.size !== 1 ? 's' : ''} selected
          </span>
        </div>

        <div className="messaging-controls">
          {/* Message Type Selector */}
          <div className="control-group">
            <label htmlFor="messageType">Message Type:</label>
            <select
              id="messageType"
              value={messageType}
              onChange={(e) => setMessageType(e.target.value)}
              className="message-type-select"
            >
              <option value="0">Select Message Type</option>
              <option value="1">Confirmation Message</option>
              <option value="2">Reminder Message</option>
              <option value="3">Custom Message</option>
              <option value="4">Sendout Payment Reminder</option>
            </select>
          </div>

          {/* Custom Message Input */}
          {messageType === '3' && (
            <div className="control-group custom-message-group">
              <label htmlFor="customMessage">Custom Message:</label>
              <textarea
                id="customMessage"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="custom-message-input"
                rows="4"
                placeholder="Enter your custom message here... (ID will be automatically appended)"
              />
              <div className="helper-text">
                The appointment ID will be automatically added to track responses
              </div>
            </div>
          )}

          {/* Message Preview */}
          {messageType !== '0' && selectedAppointments.size > 0 && (
            <div className="message-preview">
              <h4>Preview ({selectedAppointments.size} message{selectedAppointments.size !== 1 ? 's' : ''}):</h4>
              <div className="preview-messages-container">
                {appointments
                  .filter(apt => selectedAppointments.has(apt.ID))
                  .map(apt => (
                    <div key={apt.ID} className="preview-message-item">
                      <div className="preview-recipient">
                        <strong>{apt.ClientName}</strong> - {apt.PhoneNum}
                      </div>
                      <div className="preview-content">
                        {generateMessage(apt)}
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              className="refresh-button"
              onClick={fetchAppointments}
              disabled={sending}
            >
              ↻ Refresh
            </button>
            <button
              className="send-button"
              onClick={handleSendMessages}
              disabled={sending || messageType === '0' || selectedAppointments.size === 0}
            >
              {sending ? 'Sending...' : `Send to ${selectedAppointments.size} Appointment${selectedAppointments.size !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
             
 
      </div>
      
      

      {/* Message Thread Modal */}
      {messageThreadModal && (
        <MessageThreadModal
          phoneNumber={messageThreadModal.phoneNumber}
          customerName={messageThreadModal.customerName}
          appointmentId={messageThreadModal.appointmentId}
          onClose={handleCloseMessageThread}
        />
      )}
    </div>
    
  );
};

export default AppSendText;
